import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/** One barcode identity row of a variant (backend VariantBarcode). */
export interface VariantBarcode {
  id: string;
  variantId: string;
  code: string;
  isPrimary: boolean;
  isActive: boolean;
  source: string;
  createdAt: string;
  updatedAt: string;
}

export interface BarcodeRowError {
  row: number;
  column: string;
  error: string;
}

/** Bulk EAN grid: what the backend knows about one SKU (prefill columns). */
export interface SkuBarcodeLookup {
  sku: string;
  found: boolean;
  productId?: string;
  variantId?: string;
  name?: string;
  isActive?: boolean;
  barcodes: { code: string; isPrimary: boolean }[];
  hasPrice?: boolean;
}

export interface BulkBarcodeRow {
  rowNumber: number;
  sku: string;
  variantId?: string;
  code: string;
  makePrimary?: boolean;
  retireCode?: string;
}

export interface BulkBarcodeValidation {
  ok: boolean;
  totalRows: number;
  validRows: number;
  errors: BarcodeRowError[];
  warnings: BarcodeRowError[];
}

export interface BulkBarcodeResult {
  created: number;
  reactivated: number;
  unchanged: number;
  retired: number;
  warnings: BarcodeRowError[];
}

/**
 * Barcodes are IDENTITY, separate from pricing: a SKU is tagged with its
 * EAN(s) here and priced later (typically at its first GRN). Backed by
 * /variant-barcodes on the backend.
 */
@Injectable({ providedIn: 'root' })
export class VariantBarcodeService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/variant-barcodes`;

  list(variantId: string): Observable<VariantBarcode[]> {
    return this.http.get<VariantBarcode[]>(this.base, { params: { variantId } });
  }

  add(body: {
    variantId: string;
    code: string;
    isPrimary?: boolean;
    source?: string;
  }): Observable<VariantBarcode & { outcome: string; warning: string | null }> {
    return this.http.post<VariantBarcode & { outcome: string; warning: string | null }>(
      this.base,
      body,
    );
  }

  setPrimary(id: string): Observable<VariantBarcode> {
    return this.http.patch<VariantBarcode>(`${this.base}/${id}/primary`, {});
  }

  /** Replace a barcode with another in one step (old retired, primary carried over). */
  replace(id: string, code: string): Observable<VariantBarcode & { outcome: string; warning: string | null }> {
    return this.http.patch<VariantBarcode & { outcome: string; warning: string | null }>(
      `${this.base}/${id}/code`,
      { code },
    );
  }

  deactivate(id: string): Observable<VariantBarcode> {
    return this.http.patch<VariantBarcode>(`${this.base}/${id}/deactivate`, {});
  }

  /** Resolve typed SKUs to their variants and current barcodes. */
  bulkLookup(skus: string[]): Observable<SkuBarcodeLookup[]> {
    return this.http.post<SkuBarcodeLookup[]>(`${this.base}/bulk/lookup`, { skus });
  }

  /** Dry run: per-cell errors and warnings, nothing written. */
  bulkValidate(rows: BulkBarcodeRow[]): Observable<BulkBarcodeValidation> {
    return this.http.post<BulkBarcodeValidation>(`${this.base}/bulk/validate`, { rows });
  }

  /** Save every row or nothing at all. */
  bulkCommit(rows: BulkBarcodeRow[]): Observable<BulkBarcodeResult> {
    return this.http.post<BulkBarcodeResult>(`${this.base}/bulk`, { rows });
  }
}
